import type { ApplicantCreation } from 'cpool-schemas';
import type { Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ItemDeleteButton from '~/features/leadDetails/components/Overview/CustomerData/General/ItemDeleteButton.tsx';
import {
    type IncomeType,
    incomeTypeConfig,
} from '~/features/leadDetails/components/Overview/CustomerData/Income/incomeTypeConfig.ts';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard.tsx';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormTextInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormTextInput.tsx';
import { subFieldLabelClassName } from '~/features/leadDetails/components/Overview/CustomerData/shared/subFieldLabel.ts';

type IncomeItemProps = {
    index: number;
    type: IncomeType;
    onRemove: () => void;
};

export const IncomeItem: React.FC<IncomeItemProps> = ({ index, onRemove, type }) => {
    const { t } = useTranslation();
    const { control } = useFormContext<ApplicantCreation>();

    const isSecondaryEmployment = type === 'einkuenfteAusNebentaetigkeit';
    const isOtherIncome = type === 'sonstigeEinnahmen';
    const hasSubField = isSecondaryEmployment || isOtherIncome;

    return (
        <FormCard className="py-0">
            <div className="flex min-w-0 items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className={hasSubField ? 'flex flex-col gap-2' : undefined}>
                        <FormCurrencyInput
                            id={`einnahmen.${type}.${index}.betragMonatlich` as Path<ApplicantCreation>}
                            control={control}
                            label={t(incomeTypeConfig[type].label)}
                            labelClassName="h-8 w-[230px] shrink-0 justify-start"
                            min={0}
                        />
                        {isSecondaryEmployment && (
                            <FormDateInput
                                id={`einnahmen.${type}.${index}.beginnDerTaetigkeit` as Path<ApplicantCreation>}
                                label={t('LEADS.DETAILS.INCOME.START_DATE')}
                                labelClassName={subFieldLabelClassName}
                                control={control}
                            />
                        )}
                        {isOtherIncome && (
                            <FormTextInput
                                id={`einnahmen.${type}.${index}.notiz` as Path<ApplicantCreation>}
                                label={t('LEADS.DETAILS.INCOME.NOTE')}
                                labelClassName={subFieldLabelClassName}
                                control={control}
                            />
                        )}
                    </div>
                </div>
                <ItemDeleteButton iconOnly onClick={onRemove} translationPath="DELETE" />
            </div>
        </FormCard>
    );
};
